import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { crawlingService } from '../../services/crawlingService';
import { leadGenService } from '../../services/leadGeneration';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import Loading from '../common/Loading';
import ConfirmationModal from '../common/ConfirmationModal';
import GuideSystem from '../common/GuideSystem';
import FloatingGuideButton from '../common/FloatingGuideButton';
import { getGuideContent } from '../../utils/guideContent';
import { useGuide } from '../../hooks/useGuide';
import { useCrawlUpdates } from '../../hooks/useCrawlUpdates';

const CrawlingDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [recentLinks, setRecentLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  const [places, setPlaces] = useState([]);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'default'
  });

  const { isGuideVisible, hasSeenGuide, showGuide, hideGuide } = useGuide('crawling');
  const guideData = getGuideContent('crawling');

  // WebSocket real-time updates
  const { 
    isConnected: wsConnected, 
    batchProgress,
    crawlStatuses: realtimeCrawlStatuses
  } = useCrawlUpdates();

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Refresh stats when crawls complete
  useEffect(() => {
    const completedCrawls = Object.values(realtimeCrawlStatuses).filter(
      status => status.status === 'completed' || status.status === 'failed'
    );
    
    if (completedCrawls.length > 0) {
      // Refresh dashboard data after a short delay
      const timeout = setTimeout(() => {
        loadDashboardData();
      }, 2000);
      
      return () => clearTimeout(timeout);
    }
  }, [realtimeCrawlStatuses]);

  const loadDashboardData = async () => {
    setLoading(true);
    
    try {
      // Load stats
      const { data: statsData } = await crawlingService.getCrawlStats();
      if (statsData) {
        setStats(statsData);
      }

      // Load recent tasks
      const { data: tasksData } = await crawlingService.getCrawlTasks({ page_size: 5 });
      if (tasksData) {
        setRecentTasks(tasksData.results || []);
      }

      // Load recent links
      const { data: linksData } = await crawlingService.getSocialMediaLinks({ page_size: 5 });
      if (linksData) {
        setRecentLinks(linksData.results || []);
      }

      // Load places without recent crawl tasks for batch crawling
      const { data: placesData } = await leadGenService.getUserPlaces({ 
        page_size: 50,
        has_lead: 'false' // Focus on places that haven't been converted to leads yet
      });
      if (placesData) {
        setPlaces(placesData.places || []);
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    }
    
    setLoading(false);
  };

  const handleSelectPlace = (placeId) => {
    setSelectedPlaces(prev => {
      if (prev.includes(placeId)) {
        return prev.filter(id => id !== placeId);
      } else {
        return [...prev, placeId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPlaces.length === places.length) {
      setSelectedPlaces([]);
    } else {
      setSelectedPlaces(places.map(place => place.id));
    }
  };

  const startBatchCrawl = async () => {
    if (selectedPlaces.length === 0) {
      toast.error('Please select places to crawl');
      return;
    }

    setConfirmationModal({
      isOpen: true,
      title: 'Start Batch Crawl',
      message: `Start crawling ${selectedPlaces.length} selected place(s) for social media information?`,
      type: 'info',
      onConfirm: async () => {
        const { data, error } = await crawlingService.startBatchCrawl(selectedPlaces);
        
        if (data) {
          toast.success(`Batch crawl started for ${selectedPlaces.length} places`);
          setSelectedPlaces([]);
          loadDashboardData(); // Refresh data
        } else {
          toast.error('Failed to start batch crawl');
        }
        
        setConfirmationModal({ ...confirmationModal, isOpen: false });
      }
    });
  };

  const QuickStatCard = ({ title, value, subtitle, icon, color = 'blue', onClick }) => (
    <div 
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <span className="text-2xl">{icon}</span>
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && (
            <p className="text-sm text-gray-500">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Guide System */}
      {guideData && (
        <GuideSystem
          steps={guideData.steps}
          pageName={guideData.pageName}
          isVisible={isGuideVisible}
          onClose={hideGuide}
        />
      )}

      {/* Floating Guide Button */}
      <FloatingGuideButton 
        onClick={showGuide}
        hasNewFeature={!hasSeenGuide}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
      />

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-semibold text-gray-900">Crawling Dashboard</h1>
              
              {/* WebSocket Connection Indicator */}
              {wsConnected ? (
                <div className="flex items-center space-x-2 text-xs text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Live Updates</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2 text-xs text-gray-400">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  <span>Connecting...</span>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={loadDashboardData}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
              >
                Refresh
              </button>
              {selectedPlaces.length > 0 && (
                <button
                  onClick={startBatchCrawl}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                >
                  Start Crawl ({selectedPlaces.length})
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Live Batch Progress */}
        {Object.entries(batchProgress).length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">🚀 Active Batch Crawls</h3>
            {Object.entries(batchProgress).map(([batchId, progress]) => (
              <div key={batchId} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-medium text-gray-900">Batch Crawl in Progress</div>
                    {progress.current_place && (
                      <div className="text-sm text-gray-600">Currently crawling: {progress.current_place}</div>
                    )}
                  </div>
                  <div className="text-sm font-medium text-blue-600">
                    {progress.completed} / {progress.total_places} places
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-blue-600 h-3 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
                <div className="text-xs text-gray-500 mt-2 text-right font-medium">
                  {Math.round(progress.percentage)}% Complete
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <QuickStatCard
            title="Total Crawl Tasks"
            value={stats?.total_crawl_tasks?.toLocaleString() || '0'}
            subtitle={`${stats?.pending_tasks || 0} pending`}
            icon="📋"
            color="blue"
            onClick={() => navigate('/crawling/tasks')}
          />
          <QuickStatCard
            title="Social Links Found"
            value={stats?.total_social_links?.toLocaleString() || '0'}
            subtitle={`${stats?.verified_links || 0} verified`}
            icon="🔗"
            color="green"
            onClick={() => navigate('/crawling/links')}
          />
          <QuickStatCard
            title="Success Rate"
            value={`${stats?.success_rate || 0}%`}
            subtitle={`${stats?.completed_tasks || 0} completed`}
            icon="✅"
            color="purple"
            onClick={() => navigate('/crawling/stats')}
          />
          <QuickStatCard
            title="Recent Activity"
            value={stats?.recent_activity_30_days || '0'}
            subtitle="Last 30 days"
            icon="📊"
            color="orange"
            onClick={() => navigate('/crawling/stats')}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Recent Crawl Tasks */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Recent Crawl Tasks</h3>
              <button
                onClick={() => navigate('/crawling/tasks')}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                View All
              </button>
            </div>
            {recentTasks.length > 0 ? (
              <div className="space-y-3">
                {recentTasks.map((task) => (
                  <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="mr-2">{crawlingService.getStatusIcon(task.status)}</span>
                        <span className="font-medium text-gray-900 text-sm">
                          {task.place?.display_name}
                        </span>
                        <span className={`ml-2 inline-flex px-2 py-1 text-xs font-medium rounded-full ${crawlingService.getStatusColor(task.status)}`}>
                          {task.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {crawlingService.formatTimestamp(task.created_at)}
                      </div>
                    </div>
                    {task.discovered_links_count !== undefined && (
                      <div className="text-sm text-gray-600">
                        {task.discovered_links_count} links
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-4">
                No recent crawl tasks
              </div>
            )}
          </div>

          {/* Recent Social Links */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Recent Social Links</h3>
              <button
                onClick={() => navigate('/crawling/links')}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                View All
              </button>
            </div>
            {recentLinks.length > 0 ? (
              <div className="space-y-3">
                {recentLinks.map((link) => (
                  <div key={link.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="mr-2">{crawlingService.getPlatformIcon(link.link_type)}</span>
                        <span className="font-medium text-gray-900 text-sm">
                          {link.place_name}
                        </span>
                        <span 
                          className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: crawlingService.getPlatformColor(link.link_type) }}
                        >
                          {link.link_type}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 truncate">
                        {link.url}
                      </div>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${crawlingService.getVerificationStatusColor(link.verification_status)}`}>
                      {crawlingService.getVerificationStatusIcon(link.verification_status)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-4">
                No recent social links found
              </div>
            )}
          </div>
        </div>

        {/* Batch Crawling Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Start Batch Crawling</h3>
            <div className="text-sm text-gray-500">
              Select places to crawl for social media information
            </div>
          </div>

          {places.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={places.length > 0 && selectedPlaces.length === places.length}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-500">
                    {selectedPlaces.length > 0 ? `${selectedPlaces.length} selected` : 'Select all'}
                  </span>
                </div>
                <div className="text-sm text-gray-500">
                  {places.length} places available
                </div>
              </div>

              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                <div className="divide-y divide-gray-200">
                  {places.slice(0, 20).map((place) => (
                    <div key={place.id} className="p-3 hover:bg-gray-50">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedPlaces.includes(place.id)}
                          onChange={() => handleSelectPlace(place.id)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                        <div className="ml-3 flex-1">
                          <div className="font-medium text-gray-900 text-sm">
                            {place.display_name}
                          </div>
                          {place.formatted_address && (
                            <div className="text-xs text-gray-500 truncate">
                              📍 {place.formatted_address}
                            </div>
                          )}
                        </div>
                        {place.website_uri && (
                          <div className="text-xs text-green-600">
                            🌐 Has Website
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {places.length > 20 && (
                <div className="mt-3 text-center">
                  <button
                    onClick={() => navigate('/places')}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    View all {places.length} places
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Places Available</h3>
              <p className="text-gray-500 mb-4">You need to save some places before you can start crawling.</p>
              <button
                onClick={() => navigate('/search')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                Search for Places
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrawlingDashboard;